import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { PersonSummaryResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import {
  getShowArchivedRecordsPreference,
  getShowDeletedRecordsPreference,
} from '@/services/record-visibility-preferences';
import { Alert, Button, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { Avatar } from '@/components/avatar';
import { PersonForm } from './person-form';
import { ApiError } from '@/services/api-client';
import {
  activeFilterCount,
  EMPTY_PEOPLE_FILTERS,
  PeopleFilterDrawer,
  type PeopleFilterState,
} from './people-filter-drawer';
import { PeopleBulkBar } from './people-bulk-bar';
import { ImportPeopleDialog } from './import-people-dialog';

/**
 * The People directory.
 *
 * The single screen the People vertical slice terminates in, and the one church
 * staff use most.
 */
export function PeopleListPage() {
  const { t } = useI18n();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filters, setFilters] = useState<PeopleFilterState>(EMPTY_PEOPLE_FILTERS);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  /**
   * The search term lags one render behind while typing.
   *
   * Without this, every keystroke queues a request and the results flicker
   * through intermediate states. `useDeferredValue` keeps the input responsive
   * and lets React drop superseded renders.
   */
  const deferredSearch = useDeferredValue(search);
  // Controlled exclusively by My Settings — no per-page override, so a
  // record is never one accidental checkbox away from view. `ARCHIVED` and
  // `DELETED` are independent terminal statuses (see
  // `PrismaPersonRepository.buildWhere`), each governed by its own switch.
  const includeArchived = getShowArchivedRecordsPreference();
  const includeDeleted = getShowDeletedRecordsPreference();

  const query = useQuery({
    queryKey: ['people', { search: deferredSearch, includeArchived, includeDeleted, filters }],
    queryFn: ({ signal }) =>
      peopleApi.list(
        {
          search: deferredSearch || undefined,
          includeArchived,
          includeDeleted,
          status: filters.status || undefined,
          gender: filters.gender || undefined,
          maritalStatus: filters.maritalStatus || undefined,
          birthDateFrom: filters.birthDateFrom || undefined,
          birthDateTo: filters.birthDateTo || undefined,
          limit: 50,
        },
        signal,
      ),
    // Kept while a new search resolves, so the table does not empty and
    // reflow between keystrokes.
    placeholderData: (previous) => previous,
  });

  const people = query.data?.data ?? [];
  const isStale = deferredSearch !== search;

  function toggleSelected(id: string): void {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(): void {
    setSelectedIds((current) =>
      current.size === people.length ? new Set() : new Set(people.map((p) => p.id)),
    );
  }

  return (
    <div className="page people-scope">
      <header className="page-header">
        <div>
          <h1>{t('people.person.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('people.person.count', { count: people.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        {/*
          Rendered only when the permission is held. This is a usability
          decision, not a security one — the server rejects the request
          regardless of what the client draws.
        */}
        <div className="page-actions">
          {can('people.transfer.read') && (
            <Link to="/people/transfers/pending" className="btn" data-variant="secondary">
              {t('people.transfer.pendingTitle')}
            </Link>
          )}
          {can('people.followup.read') && (
            <Link to="/people/followups/pipeline" className="btn" data-variant="secondary">
              {t('people.followup.pipelineTitle')}
            </Link>
          )}
          {can('people.growthStep.read') && (
            <Link to="/people/growth/steps" className="btn" data-variant="secondary">
              {t('people.growth.stepsTitle')}
            </Link>
          )}
        </div>
        {can('people.person.import') && (
          <button type="button" className="btn" onClick={() => setIsImporting(true)}>
            {t('people.import.action')}
          </button>
        )}
        {can('people.person.create') && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            onClick={() => setIsCreating(true)}
          >
            {t('people.person.create')}
          </button>
        )}
      </header>

      <div className="people-toolbar">
        <div className="filters">
          <Field
            label={t('common.actions.search')}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('common.actions.search')}
            name="search"
          />
        </div>

        <Button
          type="button"
          variant="secondary"
          className="people-toolbar-btn"
          onClick={() => setIsFiltering(true)}
        >
          {t('people.filters.title')}
          {activeFilterCount(filters) > 0 && ` (${activeFilterCount(filters)})`}
        </Button>

        {/* In-tab SPA navigation, not `target="_blank"`: a fresh tab starts
            with no in-memory auth state at all — `sessionStorage`'s
            same-origin clone-to-new-tab only fires with an opener kept
            (no `rel="noopener"`), and even then a real login race was
            observed, not just a theoretical one. Staying in-tab sidesteps
            the whole question; the print page's own "Back" link returns
            here. A ctrl/cmd-click still opens a real new tab for anyone
            who wants one — same-origin, no `noopener`, opener kept. */}
        <Link
          className="btn"
          to={printUrl(
            '/people/print/directory',
            deferredSearch,
            includeArchived,
            includeDeleted,
            filters,
          )}
        >
          {t('people.print.directory')}
        </Link>
        <Link
          className="btn"
          to={printUrl(
            '/people/print/labels',
            deferredSearch,
            includeArchived,
            includeDeleted,
            filters,
          )}
        >
          {t('people.print.labels')}
        </Link>
      </div>

      <PeopleBulkBar
        selectedIds={selectedIds}
        people={people}
        onArchived={() => {
          setSelectedIds(new Set());
          void query.refetch();
        }}
      />

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && people.length === 0 && (
        <EmptyState
          title={search ? t('common.states.noResults') : t('common.states.empty')}
          hint={search ? undefined : t('people.person.create')}
        />
      )}

      {people.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col" className="people-checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === people.length}
                    onChange={toggleSelectAll}
                    aria-label={t('people.bulk.selectAll')}
                  />
                </th>
                <th scope="col">{t('people.person.singular')}</th>
                <th scope="col">{t('people.person.email')}</th>
                <th scope="col">{t('people.person.phone')}</th>
                <th scope="col">{t('common.labels.status')}</th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  isSelected={selectedIds.has(person.id)}
                  onToggleSelected={() => toggleSelected(person.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <PersonForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}

      {isImporting && (
        <ImportPeopleDialog
          onClose={() => {
            setIsImporting(false);
            void query.refetch();
          }}
        />
      )}

      {isFiltering && (
        <PeopleFilterDrawer
          value={filters}
          onApply={setFilters}
          onClose={() => setIsFiltering(false)}
        />
      )}
    </div>
  );
}

function PersonRow({
  person,
  isSelected,
  onToggleSelected,
}: {
  person: PersonSummaryResponse;
  isSelected: boolean;
  onToggleSelected: () => void;
}) {
  const { t } = useI18n();

  return (
    <tr data-archived={person.archivedAt ? true : undefined}>
      <td className="people-checkbox-col">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelected}
          aria-label={person.displayName}
        />
      </td>
      <td>
        {/*
          The name is the link. A separate "view" column would add a target that
          is smaller and harder to hit than the thing people already aim at.
        */}
        <Link to={`/people/${person.id}`} className="person-name people-avatar-cell">
          <Avatar photoFileId={person.photoFileId} displayName={person.displayName} size={28} />
          {person.displayName}
        </Link>
      </td>
      {/*
        Contact details are always left-to-right, whatever the page direction:
        an email address or a phone number rendered RTL is visually scrambled.
      */}
      <td dir="ltr" className="cell-mono">
        {person.primaryEmail ?? <span className="cell-empty">—</span>}
      </td>
      <td dir="ltr" className="cell-mono">
        {person.primaryPhone ?? <span className="cell-empty">—</span>}
      </td>
      <td>
        <StatusBadge status={person.status} label={t(`people.person.status.${person.status}`)} />
      </td>
    </tr>
  );
}

/** Mirrors this page's own filters into the printable page's query string,
 *  so "Print" prints what the operator was actually looking at. */
function printUrl(
  path: string,
  search: string,
  includeArchived: boolean,
  includeDeleted: boolean,
  filters: PeopleFilterState,
): string {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (includeArchived) params.set('includeArchived', 'true');
  if (includeDeleted) params.set('includeDeleted', 'true');
  if (filters.status) params.set('status', filters.status);
  if (filters.gender) params.set('gender', filters.gender);
  if (filters.maritalStatus) params.set('maritalStatus', filters.maritalStatus);
  if (filters.birthDateFrom) params.set('birthDateFrom', filters.birthDateFrom);
  if (filters.birthDateTo) params.set('birthDateTo', filters.birthDateTo);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
